/*

? First, let's import the essentials - React and Styled

*/

import { styled, useTheme } from "@mui/system";
import React from "react";

/*

? Next, let's import all the UI components we need from MUI

*/

import {
	AppBar,
	Avatar,
	Box,
	Button,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	MenuItem,
	OutlinedInput,
	Popover,
	Switch,
	Toolbar,
	Typography,
	useMediaQuery,
} from "@mui/material";

/*

? Next, let's import all the icons we need in this component

*/

import {
	AutoAwesome,
	DarkMode,
	GitHub,
	Google,
	Insights,
	KeyboardArrowDown,
	LightMode,
	Logout,
	PersonSearchOutlined,
	Search,
} from "@mui/icons-material";
import { useThemeMode } from "./ThemeContext";

/*

? Next, let's import all the utility components we need

*/

import { signIn, signOut } from "next-auth/react";
import Link from "next/link";
import SearchResultsModal from "../modal/SearchResultsModal";
import Notifications from "./Notifications";

/*

& Next, let's write a type to describe the extra props of the Navbar component

*/

type ExtraPropsForNavbar = {
	enableTranslucency?: boolean;
};
/*

& Next, let's create styled components for all the default MUI components we imported

*/

const StyledAppBar = styled(AppBar)(({ theme }) => ({
	borderBottom: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#F5F5F5"}`,
})) as typeof AppBar;

const StyledToolbar = styled(Toolbar)(({ theme }) => ({})) as typeof Toolbar;

const StyledTitle = styled(Typography)(({ theme }) => ({
	color: "#F76C35",
	fontSize: "1.125rem",
	fontWeight: 600,
	textTransform: "uppercase",
	letterSpacing: "0rem",
	lineHeight: "1.5rem",
	[theme.breakpoints.down(theme.breakpoints.values["xs"] + 40)]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.down("sm910")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "1rem",
	},
})) as typeof Typography;

const StyledTitle2 = styled(Typography)(({ theme }) => ({
	color: "#F76C35",
	fontSize: "1.5rem",
	fontWeight: 600,
	textTransform: "uppercase",
	letterSpacing: "0rem",
	lineHeight: "1.875rem",
	[theme.breakpoints.down(theme.breakpoints.values["xs"] + 40)]: {
		fontSize: "1.25rem",
	},
})) as typeof Typography;

const StyledLinkForTitle = styled(Link)(({ theme }) => ({
	textDecoration: "none",
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	// gap: "0.5rem",
	[theme.breakpoints.down("sm910")]: {
		display: "none",
	},
	[theme.breakpoints.down(theme.breakpoints.values["xs"] + 40)]: {
		display: "flex",
	},
}));

const StyledLinkForTitle2 = styled(Link)(({ theme }) => ({
	textDecoration: "none",
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "flex-start",
	gap: "0.5rem",
	margin: "0rem 0rem 0rem 0rem",
	[theme.breakpoints.down(theme.breakpoints.values["xs"] + 40)]: {
		display: "none",
	},
	[theme.breakpoints.down("md")]: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "flex-start",
		gap: "0.25rem",
		width: "100%",
	},
	[theme.breakpoints.up("sm910")]: {
		display: "none",
	},
}));

const Navbar = styled("nav", {
	shouldForwardProp: (prop) => prop !== "enableTranslucency",
})<ExtraPropsForNavbar>(({ theme, enableTranslucency }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "4rem",
	padding: "0.5rem 0.5rem 0.5rem 0.5rem",
	position: "relative",
	backgroundColor: enableTranslucency ? "transparent" : theme.palette.background.paper,
	backdropFilter: enableTranslucency ? "blur(1rem)" : "blur(0px)",
	[theme.breakpoints.down("sm910")]: {
		padding: "0.5rem 0.5rem 0.5rem 0.5rem",
	},
	[theme.breakpoints.down("sm")]: {
		padding: "0.25rem 0.75rem 0.25rem 0.5rem",
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		height: "4rem",
		gap: "0rem",
		width: "100%",
	},
	[theme.breakpoints.up("sm910")]: {
		padding: "0.5rem 0.5rem 0.5rem 0.5rem",
	},
}));

const Logo = styled("img")(({ theme }) => ({
	width: "2rem",
	height: "2rem",
	// scale: "1",
	[theme.breakpoints.down("sm")]: {
		width: "100%",
		height: "100%",
		scale: "0.5",
		// display: "none",
	},
}));

const LogoContainer = styled("div")(({ theme }) => ({
	width: "2rem",
	height: "2rem",
	borderRadius: "3rem",
	overflow: "hidden",
	[theme.breakpoints.down("sm910")]: {
		display: "none",
	},
}));

const LogoContainer2 = styled("div")(({ theme }) => ({
	width: "2rem",
	height: "2rem",
	borderRadius: "3rem",
	overflow: "hidden",
	[theme.breakpoints.down("sm")]: {
		width: "fit-content",
		padding: "0rem",
	},
	// [theme.breakpoints.up("sm910")]: {
	// 	display: "none",
	// },
}));

const LogoContainer3 = styled("div")(({ theme }) => ({
	width: "3rem",
	height: "3rem",
	borderRadius: "3rem",
	overflow: "hidden",
	[theme.breakpoints.down(theme.breakpoints.values["xs"] + 40)]: {
		display: "none",
	},
	[theme.breakpoints.up("sm")]: {
		display: "none",
	},
}));

const LinkContainer = styled("div")(({ theme }) => ({
	display: "none",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	[theme.breakpoints.down("sm910")]: {
		display: "none",
	},
	[theme.breakpoints.down("sm")]: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		gap: "0.5rem",
	},
	[theme.breakpoints.up("sm")]: {
		display: "flex",
		gap: "1rem",
	},
	[theme.breakpoints.up("md")]: {
		display: "flex",
		gap: "1rem",
	},
	[theme.breakpoints.up("lgPlus")]: {
		display: "flex",
		gap: "1.5rem",
	},
	[theme.breakpoints.up("xl")]: {
		display: "flex",
		gap: "2.5rem",
	},
}));

const SocialLinkContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	gap: "0.5rem",
}));

const StyledMenuIconButton = styled(IconButton)(({ theme }) => ({
	backgroundColor: "#F76C35",
	"&:hover": {
		backgroundColor: "#F76C35",
	},
	color: "#ffffff",
	borderRadius: "0.25rem",
	width: "2.25rem",
	height: "2.25rem",
	[theme.breakpoints.up("sm910")]: {
		display: "none",
	},
})) as typeof IconButton;

const MenuAndLogoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	gap: "0.5rem",
}));

const StyledUsername = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.primary,
	fontSize: "1.25rem",
	fontWeight: 600,
	textAlign: "right",
})) as typeof Typography;

const StyledUseremail = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.primary,
	fontSize: "0.9rem",
	fontWeight: 400,
	textAlign: "right",
})) as typeof Typography;

const StyledUserDetailsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	gap: "1rem",
	[theme.breakpoints.down("sm")]: {
		gap: "0.5rem",
	},
}));

const AskHiveButton = styled(Button)(({ theme }) => ({
	textTransform: "none",
	padding: "0.25rem 0.75rem",
	height: "1.75rem",
	gap: "0.4rem",
	borderRadius: "0.5rem",
	border: "none",
	color: "#fff",
	background: "linear-gradient(135deg, #B05911 0%, #D4731A 100%)",
	fontSize: "0.875rem",
	fontWeight: 500,
	boxShadow: "0 2px 8px rgba(176,89,17,0.25)",
	"&:hover": {
		background: "linear-gradient(135deg, #8f4a0e 0%, #B05911 100%)",
		boxShadow: "0 4px 12px rgba(176,89,17,0.35)",
	},
	"& .hive-sparkle": {
		fontSize: "1.1rem",
		color: "#fff",
		animation: "hiveSparkle 2.8s ease-in-out infinite",
	},
	"@keyframes hiveSparkle": {
		"0%, 100%": {
			transform: "scale(1) rotate(0deg)",
			filter: "drop-shadow(0 0 0px rgba(255,255,255,0))",
		},
		"50%": {
			transform: "scale(1.12) rotate(12deg)",
			filter: "drop-shadow(0 0 4px rgba(255,255,255,0.7))",
		},
	},
	[theme.breakpoints.down("sm")]: {
		padding: "0.25rem 0.5rem",
		"& .hive-label": { display: "none" },
	},
}));

const MetaAnalyticsButton = styled(Button)(({ theme }) => ({
	textTransform: "none",
	padding: "0.25rem 0.75rem",
	height: "1.75rem",
	gap: "0.4rem",
	borderRadius: "0.5rem",
	border: "1px solid rgba(176,89,17,0.35)",
	color: "#B05911",
	background: "transparent",
	fontSize: "0.875rem",
	fontWeight: 500,
	whiteSpace: "nowrap",
	"&:hover": {
		background: "rgba(176,89,17,0.08)",
		borderColor: "#B05911",
	},
	"& .meta-analytics-icon": {
		fontSize: "1.1rem",
	},
	[theme.breakpoints.down("sm")]: {
		padding: "0.25rem 0.5rem",
		"& .meta-analytics-label": { display: "none" },
	},
}));

const SearchContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	color: "#FC8019",
	[theme.breakpoints.down("sm")]: {
		width: "9rem",
	},
	// width: "15rem",
	// gap: "0.5rem",
}));

const CenterSearchWrapper = styled("div")(({ theme }) => ({
	position: "absolute",
	left: "50%",
	top: "50%",
	transform: "translate(-50%, -50%)",
	width: "20rem",
	[theme.breakpoints.down("sm")]: {
		display: "none",
	},
}));

const UserCardButton = styled(Button)(({ theme }) => ({
	textTransform: "none",
	padding: "0.25rem 0.5rem",
	gap: "0.5rem",
	borderRadius: "0.5rem",
	border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#E0E0E0"}`,
	color: theme.palette.text.primary,
	backgroundColor: "transparent",
	"&:hover": {
		backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
		borderColor: theme.palette.mode === "dark" ? theme.palette.divider : "#E0E0E0",
	},
	[theme.breakpoints.down("sm")]: {
		padding: "0.25rem",
	},
}));

const UserCardText = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	lineHeight: 1.2,
	[theme.breakpoints.down("sm")]: {
		display: "none",
	},
}));

const UserCardName = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: 500,
	color: theme.palette.text.primary,
	lineHeight: 1.2,
}));

const UserCardEmail = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	color: theme.palette.text.secondary,
	lineHeight: 1.2,
}));

const PopoverBody = styled(Box)(({ theme }) => ({
	padding: "0.5rem",
	minWidth: "16rem",
	display: "flex",
	flexDirection: "column",
}));

const ThemeToggleRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
	padding: "0.5rem 0.75rem",
}));

const LayoutHeader = ({
	session,
	new_notification,
	showNotification,
	setShowNotification,
}: {
	session?: any;
	new_notification?: any;
	showNotification?: boolean;
	setShowNotification?: any;
}) => {
	/*

  & Next, let's create a local state to hold the current scroll position

  */

	const [scrollPosition, setScrollPosition] = React.useState<number>(0);

	const [phoneNumber, setPhoneNumber] = React.useState<string>("");

	const [searchExpanded, setSearchExpanded] = React.useState<boolean>(false);

	const searchInputRef = React.useRef<HTMLInputElement>(null);

	const [searchResults, setSearchResults] = React.useState<any>();

	const [modalOpen, setModalOpen] = React.useState<boolean>(false);

	const [searchVisible, setSearchVisible] = React.useState<boolean>(false);

	const [userMenuAnchor, setUserMenuAnchor] = React.useState<HTMLElement | null>(null);
	const userMenuOpen = Boolean(userMenuAnchor);

	const [signOutDialogOpen, setSignOutDialogOpen] = React.useState<boolean>(false);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm910"));
	const { mode, toggleTheme } = useThemeMode();

	const accessLevel: string[] = session?.accessLevel || [];
	const isSuperSuperAdmin =
		accessLevel.includes("super_super_admin") || (accessLevel.includes("admin") && accessLevel.includes("marketing"));

	const handleSearch = () => {
		fetch(process.env.PRODUCTION_API_URL + `search-phone-number`, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain",
			},
			body: JSON.stringify({ phone_number: phoneNumber }),
		})
			.then((response) => response.json())
			.then((data) => {
				setSearchResults(data ? data.payload[0] : null);
				setModalOpen(data ? (data.payload ? true : false) : false);
			});
	};

	/*

  & Next, let's write a function to update the scroll position state on change of the scroll position

  */

	const onChangeScrollPosition: () => void = () => {
		setScrollPosition(window.scrollY);
	};

	/*

  & Next, let's write a trigger-once useEffect hook that will register and remove our scroll event listener

  */

	React.useEffect(() => {
		window.addEventListener("scroll", onChangeScrollPosition, {
			passive: true,
		});
		return () => {
			window.removeEventListener("scroll", onChangeScrollPosition);
		};
	}, []);
	return (
		<React.Fragment>
			<SearchResultsModal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
				}}
				searchResults={searchResults}
			/>
			<StyledAppBar
				position="fixed"
				color="transparent"
				elevation={scrollPosition === 0 ? 0 : 1}
			>
				<Navbar enableTranslucency={scrollPosition !== 0}>
					<MenuAndLogoContainer>
						<StyledLinkForTitle
							href="/"
							rel="noopener"
							aria-label="Beegru Control Panel"
							title="Beegru Control Panel"
						>
							<LogoContainer>
								<Logo
									src={(process.env.CDN_URL || "") + "images/icons/logo/new_logo.jpeg"}
									alt="Beegru Control Panel"
									width={48}
									height={48}
								/>
							</LogoContainer>
							<StyledTitle
								variant="h1"
								component="span"
							>
								Beegru Control Panel
							</StyledTitle>
						</StyledLinkForTitle>
					</MenuAndLogoContainer>
					<StyledLinkForTitle2
						href="/"
						rel="noopener"
						aria-label="Beegru Marketplace Control Panel"
						title="Beegru Marketplace Control Panel"
					>
						<LogoContainer2>
							<Logo
								src={(process.env.CDN_URL || "") + "images/icons/logo/favicon_192.png"}
								alt="Beegru Control Panel"
								width={48}
								height={48}
							/>
						</LogoContainer2>
						<StyledTitle
							variant="h1"
							component="h1"
						>
							Beegru MCP
						</StyledTitle>
					</StyledLinkForTitle2>

					{/* <LogoContainer3>
						<Logo
							src={(process.env.CDN_URL || "") + "images/icons/logo/favicon_192.png"}
							alt="Beegru Marketplace Control Panel"
							width={48}
							height={48}
						/>
					</LogoContainer3> */}
					<LinkContainer>
						<SocialLinkContainer>
							{session ? (
								<StyledUserDetailsContainer>
									{isSuperSuperAdmin && (
										<AskHiveButton
											aria-label="Ask Hive"
											onClick={() => window.dispatchEvent(new Event("toggle-hive-chat"))}
										>
											<AutoAwesome className="hive-sparkle" />
											<span className="hive-label">Ask Hive</span>
										</AskHiveButton>
									)}
									{isSuperSuperAdmin && (
										<Link
											href="/meta-analytics"
											style={{ textDecoration: "none" }}
										>
											<MetaAnalyticsButton aria-label="Meta Analytics">
												<Insights className="meta-analytics-icon" />
												<span className="meta-analytics-label">Meta Analytics</span>
											</MetaAnalyticsButton>
										</Link>
									)}
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Collapse
											in={searchExpanded}
											orientation="horizontal"
											timeout={250}
										>
											<OutlinedInput
												sx={{
													width: "16rem",
													height: "2.25rem",
													padding: "0rem 0.5rem 0rem 0.75rem",
													borderRadius: "0.5rem",
													background: "inherit",
													color: "#616161",
													"& .MuiOutlinedInput-input": {
														padding: "0rem",
														background: "transparent",
														color: "#616161",
														"&::placeholder": {
															color: "#9E9E9E",
															opacity: 1,
														},
													},
													"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
														borderColor: "#616161",
														borderWidth: "1px",
													},
													"&:hover .MuiOutlinedInput-notchedOutline": {
														borderColor: "#616161",
													},
												}}
												placeholder="Search by phone number..."
												inputRef={searchInputRef}
												value={phoneNumber}
												error={phoneNumber.length > 0 && phoneNumber.length < 10}
												onBlur={() => {
													if (!phoneNumber) {
														setSearchExpanded(false);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														handleSearch();
													}
												}}
												onChange={(e) => {
													const numericValue = e.target.value.replace(/\D/g, "");
													if (numericValue.length > 10) {
														return;
													}
													setPhoneNumber(numericValue);
												}}
												endAdornment={
													<InputAdornment position="end">
														<IconButton
															aria-label="search by phone number"
															onClick={handleSearch}
															edge="end"
															disabled={phoneNumber.length < 10}
														>
															<PersonSearchOutlined
																sx={{
																	color: "#9E9E9E",
																	fontSize: "1.5rem",
																}}
															/>
														</IconButton>
													</InputAdornment>
												}
											/>
										</Collapse>
										{!searchExpanded && (
											<IconButton
												aria-label="Search by phone number"
												onClick={() => {
													setSearchExpanded(true);
													setTimeout(() => searchInputRef.current?.focus(), 50);
												}}
												sx={{ color: "#616161" }}
											>
												<Search sx={{ fontSize: "1.5rem" }} />
											</IconButton>
										)}
									</Box>
									{session && session.accessLevel && session.accessLevel === "super_super_admin" ? (
										<Notifications
											session={session}
											new_notification={new_notification}
											showNotification={showNotification}
											setShowNotification={setShowNotification}
										/>
									) : null}
									{session && session.user ? (
										<>
											<UserCardButton
												onClick={(e) => setUserMenuAnchor(e.currentTarget)}
												aria-label="Open user menu"
											>
												<Avatar
													src={session.user.image || "https://www.w3schools.com/howto/img_avatar.png"}
													alt={session.user.name || "User"}
													imgProps={{ referrerPolicy: "no-referrer" }}
													sx={{ width: "2rem", height: "2rem" }}
												/>
												<UserCardText>
													<UserCardName>{session.user.name || "User"}</UserCardName>
													<UserCardEmail>
														{session.isGitHub ? session.username || "" : session.user.email || ""}
													</UserCardEmail>
												</UserCardText>
												<KeyboardArrowDown
													fontSize="small"
													sx={{ color: theme.palette.text.secondary }}
												/>
											</UserCardButton>
											<Popover
												open={userMenuOpen}
												anchorEl={userMenuAnchor}
												onClose={() => setUserMenuAnchor(null)}
												anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
												transformOrigin={{ vertical: "top", horizontal: "right" }}
												slotProps={{
													paper: {
														sx: {
															mt: "0.5rem",
															borderRadius: "0.5rem",
															border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#E0E0E0"}`,
															boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
														},
													},
												}}
											>
												<PopoverBody>
													<ThemeToggleRow>
														<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
															{mode === "dark" ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
															<Typography variant="body2">Dark mode</Typography>
														</Box>
														<Switch
															checked={mode === "dark"}
															onChange={toggleTheme}
															size="small"
														/>
													</ThemeToggleRow>
													<Divider sx={{ my: "0.25rem" }} />
													<MenuItem
														onClick={() => {
															setUserMenuAnchor(null);
															setSignOutDialogOpen(true);
														}}
														sx={{
															gap: "0.5rem",
															borderRadius: "0.25rem",
															padding: "0.5rem 0.75rem",
														}}
													>
														<Logout fontSize="small" />
														<Typography variant="body2">Sign out</Typography>
													</MenuItem>
												</PopoverBody>
											</Popover>
											<Dialog
												open={signOutDialogOpen}
												onClose={() => setSignOutDialogOpen(false)}
												PaperProps={{
													sx: {
														borderRadius: "0.75rem",
														minWidth: "22rem",
													},
												}}
											>
												<DialogTitle sx={{ fontWeight: 600 }}>Sign out?</DialogTitle>
												<DialogContent>
													<DialogContentText>
														You&apos;ll need to sign in again to access the control panel.
													</DialogContentText>
												</DialogContent>
												<DialogActions sx={{ padding: "0.5rem 1.5rem 1rem" }}>
													<Button
														onClick={() => setSignOutDialogOpen(false)}
														sx={{
															textTransform: "none",
															color: theme.palette.text.secondary,
														}}
													>
														Stay signed in
													</Button>
													<Button
														onClick={() => {
															setSignOutDialogOpen(false);
															signOut();
														}}
														variant="contained"
														sx={{
															textTransform: "none",
															padding: "0.25rem 0.75rem",
															height: "1.75rem",
															gap: "0.4rem",
															borderRadius: "0.5rem",
															border: "none",
															color: "#fff",
															background: "#EF5350",
															fontSize: "0.875rem",
															fontWeight: 500,
															boxShadow: "0 2px 8px rgba(229,115,115,0.25)",
															"&:hover": {
																background: "#D9534F",
																boxShadow: "0 4px 12px rgba(229,115,115,0.35)",
															},
														}}
													>
														Sign out
													</Button>
												</DialogActions>
											</Dialog>
										</>
									) : !session || !session.isGitHub ? (
										<Button
											startIcon={<Google />}
											variant="contained"
											size="large"
											color="primary"
											sx={{
												textTransform: "none",
												color: "#FFFFFF",
												backgroundColor: "#fc8019",
												"&:hover": {
													backgroundColor: "#e67300",
												},
												fontSize: "1rem",
												padding: "0.5rem 1rem",
												[theme.breakpoints.down("sm")]: {
													display: "none",
												},
											}}
											onClick={() =>
												signIn("google", {
													callbackUrl: process.env.PRODUCTION_URL || "",
												})
											}
										>
											{!isMobile ? "Sign In with Google" : ""}
										</Button>
									) : (
										<Button
											startIcon={<GitHub />}
											variant="contained"
											size="large"
											color="primary"
											sx={{
												textTransform: "none",
												color: "#FFFFFF",
												fontSize: "1rem",
												padding: "0.5rem 1rem",
												[theme.breakpoints.down("sm")]: {
													display: "none",
												},
											}}
											onClick={() =>
												signIn("github", {
													callbackUrl: process.env.PRODUCTION_URL || "",
												})
											}
										>
											{!isMobile ? "Sign In with GitHub" : ""}
										</Button>
									)}
								</StyledUserDetailsContainer>
							) : undefined}
						</SocialLinkContainer>
					</LinkContainer>
				</Navbar>
			</StyledAppBar>
		</React.Fragment>
	);
};

/*

& Finally, let's export the component as default

*/

export default LayoutHeader;
